"use client"

import React from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  useTheme,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  Divider,
  alpha,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PeopleIcon from "@mui/icons-material/People"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import AnalyticsIcon from "@mui/icons-material/Analytics"
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
  Line,
  PieChart,
  Pie,
} from "recharts"
import dayjs from "dayjs"

/**
 * COMPONENTE AUXILIAR: TabPanel
 *
 * Funcionalidad:
 * - Maneja la visualización condicional de contenido basado en el tab activo
 * - Implementa accesibilidad con ARIA roles y propiedades
 * - Solo renderiza el contenido cuando el tab está activo (optimización de performance)
 *
 * Props:
 * - children: Contenido a mostrar en el panel
 * - value: Valor del tab actualmente activo
 * - index: Índice del tab actual
 * - other: Props adicionales para el contenedor
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chart-tabpanel-${index}`}
      aria-labelledby={`chart-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  )
}

/**
 * FUNCIÓN AUXILIAR: a11yProps
 *
 * Funcionalidad:
 * - Genera propiedades de accesibilidad para los tabs
 * - Conecta cada tab con su panel correspondiente mediante ARIA
 * - Mejora la navegación para usuarios con tecnologías asistivas
 */
function a11yProps(index) {
  return {
    id: `chart-tab-${index}`,
    "aria-controls": `chart-tabpanel-${index}`,
  }
}

/**
 * COMPONENTE PRINCIPAL: GraficasVentasModal
 *
 * Propósito:
 * Este modal presenta un dashboard completo de análisis de ventas generales,
 * enfocado en transacciones completas (no items individuales).
 *
 * Funcionalidades principales:
 * 1. Análisis de clientes top por volumen de ventas
 * 2. Evolución temporal de ventas (últimos 14 días)
 * 3. Distribución por medios de pago
 * 4. Performance por ubicación/sucursal
 * 5. Análisis por canales de entidad (puntos de venta)
 * 6. Métricas ejecutivas (KPIs principales)
 *
 * Props:
 * - open: Boolean que controla la visibilidad del modal
 * - handleClose: Función callback para cerrar el modal
 * - data: Array de objetos con datos de transacciones de venta
 * - loading: Boolean que indica si los datos están cargando
 */
const GraficasVentasModal = ({ open, handleClose, data, loading = false }) => {
  const theme = useTheme()
  const [tabValue, setTabValue] = React.useState(0)

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  /**
   * FUNCIÓN PRINCIPAL DE PROCESAMIENTO: procesarDatos
   *
   * Funcionalidad:
   * - Procesa el array de transacciones para generar datasets optimizados
   * - Agrupa y suma datos por diferentes dimensiones (cliente, fecha, medio de pago, ubicación, canal)
   * - Calcula métricas derivadas (promedios, totales, conteos)
   * - Filtra y ordena resultados para visualización
   * - Maneja casos edge (datos faltantes, valores nulos)
   *
   * Retorna objeto con datasets procesados para cada tipo de gráfico
   */
  const procesarDatos = React.useMemo(() => {
    if (!data || data.length === 0) {
      return {
        porCliente: [],
        porFecha: [],
        porMedioPago: [],
        porUbicacion: [],
        porCanal: [], // Added channel analytics data
        metricas: {
          totalVentas: 0,
          totalTransacciones: 0,
          ticketPromedio: 0,
          clientesUnicos: 0,
          ubicacionesActivas: 0,
          canalesActivos: 0, // Added active channels metric
        },
      }
    }

    // Paso 2: Cálculo de métricas generales del negocio
    const clienteMap = new Map()
    const ubicacionMap = new Map()

    let totalVentasCalculado = 0

    const mediosPagoMap = new Map()
    const mediosPagoTransacciones = new Map()

    const fechasMap = new Map()
    const transaccionesPorFecha = new Map()

    const clientesTransacciones = new Map()
    const clientesMap = new Map()

    data.forEach((item) => {
      // Total ventas
      totalVentasCalculado += item.montoTotal

      // Clientes
      const clienteKey = item.entidad?.descripcion || "Sin cliente"
      if (!clienteMap.has(clienteKey)) {
        clienteMap.set(clienteKey, 0)
      }
      clienteMap.set(clienteKey, clienteMap.get(clienteKey) + item.montoTotal)

      const montoActualCliente = clientesMap.get(clienteKey) || 0
      const transaccionesActualCliente = clientesTransacciones.get(clienteKey) || 0
      clientesMap.set(clienteKey, montoActualCliente + item.montoTotal)
      clientesTransacciones.set(clienteKey, transaccionesActualCliente + 1)

      // Ubicaciones
      const ubicacionKey = item.ubicacion?.descripcion || "Sin ubicación"
      if (!ubicacionMap.has(ubicacionKey)) {
        ubicacionMap.set(ubicacionKey, 0)
      }
      ubicacionMap.set(ubicacionKey, ubicacionMap.get(ubicacionKey) + item.montoTotal)

      // Medios de pago
      if (item.pagos && item.pagos.length > 0) {
        item.pagos.forEach((pago) => {
          const medioPagoKey = pago.medioDePago || "Desconocido"
          const montoActual = mediosPagoMap.get(medioPagoKey) || 0
          const transaccionesActual = mediosPagoTransacciones.get(medioPagoKey) || 0
          mediosPagoMap.set(medioPagoKey, montoActual + pago.montoTotal)
          mediosPagoTransacciones.set(medioPagoKey, transaccionesActual + 1)
        })
      }

      // Fechas
      const fecha = dayjs(item.fechaHoraCreacion).format("DD/MM/YYYY")
      const montoActualFecha = fechasMap.get(fecha) || 0
      const transaccionesActualFecha = transaccionesPorFecha.get(fecha) || 0
      fechasMap.set(fecha, montoActualFecha + item.montoTotal)
      transaccionesPorFecha.set(fecha, transaccionesActualFecha + 1)
    })

    // Paso 3: Procesamiento de datos por cliente
    const porCliente = Array.from(clientesMap.entries())
      .map(([name, value]) => ({
        name,
        value,
        transacciones: clientesTransacciones.get(name),
        ticketPromedio: value / clientesTransacciones.get(name),
      }))
      .sort((a, b) => b.value - a.value) // Ordenamiento descendente por ventas
      .slice(0, 10) // Top 10 para visualización óptima

    // Paso 4: Análisis temporal de ventas
    const porFecha = Array.from(fechasMap.entries())
      .map(([date, amount]) => ({
        date,
        amount,
        transacciones: transaccionesPorFecha.get(date),
        ticketPromedio: amount / transaccionesPorFecha.get(date),
      }))
      .sort((a, b) => dayjs(a.date, "DD/MM/YYYY").unix() - dayjs(b.date, "DD/MM/YYYY").unix())
      .slice(-14) // Últimos 14 días para análisis de tendencias

    // Paso 5: Análisis por medios de pago
    const porMedioPago = Array.from(mediosPagoMap.entries()).map(([name, value]) => ({
      name,
      value,
      transacciones: mediosPagoTransacciones.get(name),
      participacion: (value / totalVentasCalculado) * 100, // Porcentaje del total
    }))

    // Paso 6: Análisis por ubicación/sucursal
    const porUbicacion = Array.from(ubicacionMap.entries()).map(([name, value]) => ({
      name,
      value,
      transacciones: data.filter((item) => item.ubicacion?.descripcion === name).length,
      ticketPromedio: value / data.filter((item) => item.ubicacion?.descripcion === name).length,
    }))

    /**
     * PROCESAMIENTO POR CANAL DE ENTIDAD
     *
     * Funcionalidad:
     * - Agrupa transacciones por canal de entidad (punto de venta)
     * - Calcula métricas por canal: ventas totales, cantidad de transacciones, ticket promedio
     * - Identifica canales más productivos y menos productivos
     * - Analiza distribución de clientes por canal
     */
    const canalMap = new Map()

    data.forEach((transaccion) => {
      const canal = transaccion.entidad?.canalEntidad
      if (!canal) return // Skip transactions without channel data

      const canalKey = canal.descripcion || `Canal ${canal.id}`
      const monto = Math.abs(transaccion.montoTotal || 0)

      if (!canalMap.has(canalKey)) {
        canalMap.set(canalKey, {
          name: canalKey,
          id: canal.id,
          value: 0,
          transacciones: 0,
          clientesUnicos: new Set(),
        })
      }

      const canalData = canalMap.get(canalKey)
      canalData.value += monto
      canalData.transacciones += 1
      canalData.clientesUnicos.add(transaccion.entidad?.descripcion || "Cliente Genérico")
    })

    const porCanal = Array.from(canalMap.values())
      .map((canal) => ({
        ...canal,
        ticketPromedio: canal.transacciones > 0 ? canal.value / canal.transacciones : 0,
        clientesUnicos: canal.clientesUnicos.size,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)

    // Clean up Set objects for serialization
    porCanal.forEach((canal) => {
      delete canal.clientesUnicos
    })

    // Paso 7: Análisis de tendencias y patrones
    const tendencias =
      porFecha.length > 1
        ? {
            crecimiento: ((porFecha[porFecha.length - 1].amount - porFecha[0].amount) / porFecha[0].amount) * 100,
            mejorDia: porFecha.reduce((max, dia) => (dia.amount > max.amount ? dia : max), porFecha[0]),
            promedioSemanal: porFecha.reduce((sum, dia) => sum + dia.amount, 0) / porFecha.length,
          }
        : {}

    // Consolidación de métricas para el dashboard ejecutivo
    const metricas = {
      totalVentas: totalVentasCalculado,
      totalTransacciones: data.length,
      ticketPromedio: data.length > 0 ? totalVentasCalculado / data.length : 0,
      clientesUnicos: clienteMap.size,
      ubicacionesActivas: ubicacionMap.size,
      canalesActivos: canalMap.size, // Added active channels count
    }

    return {
      porCliente,
      porFecha,
      porMedioPago,
      porUbicacion,
      porCanal, // Added channel data
      metricas,
    }
  }, [data])

  const { porCliente, porFecha, porMedioPago, porUbicacion, porCanal, metricas } = procesarDatos

  const hasChannelData = porCanal && porCanal.length > 0

  const COLORS = [
    "#3B82F6", // Blue - Color primario para elementos principales
    "#10B981", // Emerald - Para métricas positivas y crecimiento
    "#F59E0B", // Amber - Para alertas y métricas de atención
    "#EF4444", // Red - Para métricas críticas o negativas
    "#8B5CF6", // Violet - Para diferenciación de categorías
    "#06B6D4", // Cyan - Para datos secundarios
    "#84CC16", // Lime - Para métricas de rendimiento
    "#F97316", // Orange - Para destacar elementos importantes
    "#EC4899", // Pink - Para categorías especiales
    "#6B7280", // Gray - Para elementos neutros
  ]

  /**
   * FUNCIÓN UTILITARIA: formatCurrency
   *
   * Propósito:
   * - Formatea números como moneda argentina (ARS)
   * - Elimina decimales para mejor legibilidad en dashboards
   * - Utiliza separadores de miles localizados
   *
   * Configuración:
   * - Locale: es-AR (español argentino)
   * - Currency: ARS (peso argentino)
   * - MaximumFractionDigits: 0 (sin decimales)
   */
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(value)
  }

  /**
   * COMPONENTE: MetricCard
   *
   * Funcionalidad:
   * - Presenta métricas clave en formato de tarjeta visual
   * - Incluye iconografía temática para identificación rápida
   * - Efectos hover para interactividad
   * - Gradientes sutiles para profundidad visual
   *
   * Props:
   * - title: Título descriptivo de la métrica
   * - value: Valor numérico o texto a mostrar
   * - icon: Componente de icono de Material-UI
   * - color: Color temático para la tarjeta
   * - subtitle: Información adicional opcional
   *
   * Diseño:
   * - Gradiente sutil con el color temático
   * - Borde con transparencia del color
   * - Animación de elevación en hover
   * - Sombra dinámica basada en el color
   */
  const MetricCard = ({ title, value, icon, color, subtitle }) => (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 25px ${color}20`,
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: `${color}20`,
              color: color,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: color }}>
            {value}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  )

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          overflow: "hidden",
        },
      }}
    >
      {/* 
        HEADER DEL MODAL
        
        Funcionalidad:
        - Presenta título y descripción del dashboard
        - Incluye botón de cierre con accesibilidad
        - Gradiente de fondo para impacto visual profesional
        - Iconografía de analytics para contexto inmediato
      */}
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          background: "linear-gradient(135deg, #8B5CF6 0%, #06B6D4 100%)",
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AnalyticsIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              Dashboard de Análisis de Ventas
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Análisis completo de transacciones y performance comercial
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "white",
            bgcolor: "rgba(255,255,255,0.1)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: "80vh", overflow: "auto" }}>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* 
          ESTADO DE CARGA
          
          Funcionalidad:
          - Muestra spinner y mensajes informativos durante la carga
          - Centra el contenido vertical y horizontalmente
          - Proporciona feedback visual del progreso
        */}
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
                gap: 2,
              }}
            >
              <CircularProgress size={60} sx={{ color: "#8B5CF6" }} />
              <Typography variant="h6" color="text.secondary">
                Procesando datos de análisis...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Obteniendo métricas y generando visualizaciones
              </Typography>
            </Box>
          ) : (
            <>
              {/* 
              PANEL DE MÉTRICAS EJECUTIVAS
              
              Funcionalidad:
              - Presenta KPIs principales en formato de tarjetas
              - Grid responsivo que se adapta a diferentes pantallas
              - Cada métrica tiene su color temático e icono
              - Información crítica visible de inmediato
              
              Métricas incluidas:
              1. Ventas Totales - Ingresos totales del período
              2. Transacciones - Cantidad total de operaciones
              3. Ticket Promedio - Valor promedio por transacción
              4. Clientes Únicos - Base de clientes activos
              5. Ubicaciones Activas - Puntos de venta operativos
            */}
              <Box sx={{ mb: 4, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "#1e293b" }}>
                  📊 Resumen Ejecutivo
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <MetricCard
                      title="Ventas Totales"
                      value={formatCurrency(metricas.totalVentas)}
                      icon={<AttachMoneyIcon />}
                      color="#10B981"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <MetricCard
                      title="Transacciones"
                      value={metricas.totalTransacciones?.toLocaleString()}
                      icon={<TrendingUpIcon />}
                      color="#3B82F6"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <MetricCard
                      title="Ticket Promedio"
                      value={formatCurrency(metricas.ticketPromedio)}
                      icon={<AnalyticsIcon />}
                      color="#F59E0B"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <MetricCard
                      title="Clientes Únicos"
                      value={metricas.clientesUnicos?.toLocaleString()}
                      icon={<PeopleIcon />}
                      color="#8B5CF6"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <MetricCard
                      title="Ubicaciones Activas"
                      value={metricas.ubicacionesActivas?.toLocaleString()}
                      icon={<LocationOnIcon />}
                      color="#EF4444"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <MetricCard
                      title="Canales Activos"
                      value={metricas.canalesActivos?.toLocaleString()}
                      icon={<LocationOnIcon />}
                      color="#EF4444"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* 
              SISTEMA DE NAVEGACIÓN POR TABS
              
              Funcionalidad:
              - Organiza diferentes análisis en pestañas
              - Navegación horizontal con scroll automático
              - Accesibilidad completa con ARIA
              - Diseño consistente con el tema del dashboard
              
              Tabs disponibles:
              1. Top Clientes - Análisis de clientes más valiosos
              2. Evolución Temporal - Tendencias de ventas en el tiempo
              3. Medios de Pago - Distribución por formas de pago
              4. Performance por Ubicación - Análisis por sucursal/punto de venta
            */}
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  backgroundColor: "#f8fafc",
                  p: 1,
                }}
              >
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="chart tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    "& .MuiTab-root": {
                      fontWeight: 600,
                      textTransform: "none",
                      minHeight: 48,
                    },
                  }}
                >
                  <Tab label="🏆 Top Clientes" {...a11yProps(0)} />
                  <Tab label="📈 Evolución Temporal" {...a11yProps(1)} />
                  <Tab label="💳 Medios de Pago" {...a11yProps(2)} />
                  <Tab label="📍 Performance por Ubicación" {...a11yProps(3)} />
                  {hasChannelData && <Tab label="🏪 Análisis por Canales" {...a11yProps(4)} />}
                </Tabs>
              </Box>

              {/* 
              TAB 1: ANÁLISIS DE TOP CLIENTES
              
              Funcionalidad:
              - Gráfico combinado (ComposedChart) que muestra ventas y transacciones
              - Ranking lateral con métricas detalladas
              - Identificación de clientes más valiosos
              - Análisis de comportamiento de compra
              
              Visualizaciones:
              - Barras: Volumen de ventas por cliente
              - Línea: Cantidad de transacciones por cliente
              - Lista: Ranking detallado con ticket promedio
              
              Insights proporcionados:
              - Clientes que generan más ingresos
              - Frecuencia de compra por cliente
              - Ticket promedio individual
              - Identificación de clientes VIP
            */}
              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={3} sx={{ p: 3 }}>
                  <Grid item xs={12} lg={8}>
                    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                        🏆 Top 10 Clientes por Volumen de Ventas
                      </Typography>
                      <ResponsiveContainer width="100%" height={400}>
                        <ComposedChart data={porCliente} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis
                            dataKey="name"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fontSize: 11, fill: "#64748b" }}
                            interval={0}
                          />
                          <YAxis
                            yAxisId="ventas"
                            tickFormatter={(value) => formatCurrency(value)}
                            width={100}
                            tick={{ fill: "#64748b" }}
                          />
                          <YAxis yAxisId="transacciones" orientation="right" tick={{ fill: "#64748b" }} />
                          <Tooltip
                            formatter={(value, name) => [
                              name === "value" ? formatCurrency(value) : value,
                              name === "value" ? "Ventas" : "Transacciones",
                            ]}
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Bar yAxisId="ventas" dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Ventas">
                            {porCliente.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                          <Line
                            yAxisId="transacciones"
                            type="monotone"
                            dataKey="transacciones"
                            stroke="#EF4444"
                            strokeWidth={3}
                            dot={{ fill: "#EF4444", strokeWidth: 2, r: 4 }}
                            name="Transacciones"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", height: "100%" }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                        📊 Ranking Detallado
                      </Typography>
                      <List sx={{ maxHeight: 350, overflow: "auto" }}>
                        {porCliente.map((cliente, index) => (
                          <React.Fragment key={cliente.name}>
                            <ListItem sx={{ px: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                <Chip
                                  label={index + 1}
                                  size="small"
                                  sx={{
                                    bgcolor: COLORS[index % COLORS.length],
                                    color: "white",
                                    fontWeight: 700,
                                    mr: 2,
                                    minWidth: 32,
                                  }}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                                    {cliente.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatCurrency(cliente.value)} • {cliente.transacciones} transacciones
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Ticket promedio: {formatCurrency(cliente.ticketPromedio)}
                                  </Typography>
                                </Box>
                              </Box>
                            </ListItem>
                            {index < porCliente.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* 
              TAB 2: EVOLUCIÓN TEMPORAL
              
              Funcionalidad:
              - Análisis de tendencias de ventas en los últimos 14 días
              - Gráfico combinado con área y barras
              - Identificación de patrones temporales
              - Análisis de performance diaria
              
              Visualizaciones:
              - Área: Evolución de ventas con gradiente
              - Barras: Cantidad de transacciones por día
              - Tooltips: Información detallada por fecha
              
              Insights proporcionados:
              - Tendencias de crecimiento o decrecimiento
              - Días de mayor actividad comercial
              - Patrones estacionales o cíclicos
              - Correlación entre volumen y cantidad de transacciones
            */}
              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={3} sx={{ p: 3 }}>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                        📈 Evolución de Ventas
                      </Typography>
                      <ResponsiveContainer width="100%" height={400}>
                        <ComposedChart data={porFecha} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                          <defs>
                            <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8} />
                              <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.1} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="date" tick={{ fill: "#64748b" }} />
                          <YAxis
                            yAxisId="ventas"
                            tickFormatter={(value) => formatCurrency(value)}
                            width={100}
                            tick={{ fill: "#64748b" }}
                          />
                          <YAxis yAxisId="transacciones" orientation="right" tick={{ fill: "#64748b" }} />
                          <Tooltip
                            formatter={(value, name) => [
                              name === "amount" ? formatCurrency(value) : value,
                              name === "amount" ? "Ventas" : "Transacciones",
                            ]}
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Area
                            yAxisId="ventas"
                            type="monotone"
                            dataKey="amount"
                            stroke="#8B5CF6"
                            strokeWidth={3}
                            fillOpacity={1}
                            fill="url(#colorAmount)"
                            name="Ventas"
                          />
                          <Bar
                            yAxisId="transacciones"
                            dataKey="transacciones"
                            fill="#10B981"
                            opacity={0.7}
                            radius={[2, 2, 0, 0]}
                            name="Transacciones"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* 
              TAB 3: MEDIOS DE PAGO
              
              Funcionalidad:
              - Análisis de distribución por formas de pago
              - Gráfico de barras horizontales para mejor legibilidad
              - Panel lateral con análisis detallado
              - Cálculo de participación de mercado
              
              Visualizaciones:
              - Barras horizontales: Volumen por medio de pago
              - Lista detallada: Participación porcentual y métricas
              - Indicadores visuales: Colores temáticos por medio
              
              Insights proporcionados:
              - Medios de pago más utilizados
              - Participación porcentual de cada medio
              - Cantidad de transacciones por medio
              - Identificación de tendencias de pago
            */}
              <TabPanel value={tabValue} index={2}>
                <Grid container spacing={3} sx={{ p: 3 }}>
                  <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                        💳 Distribución por Medios de Pago
                      </Typography>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                          data={porMedioPago}
                          layout="vertical"
                          margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis
                            type="number"
                            tickFormatter={(value) => formatCurrency(value)}
                            tick={{ fill: "#64748b" }}
                          />
                          <YAxis dataKey="name" type="category" width={120} tick={{ fill: "#64748b", fontSize: 12 }} />
                          <Tooltip
                            formatter={(value) => [formatCurrency(value), "Monto"]}
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {porMedioPago.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", height: "100%" }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                        📊 Análisis Detallado
                      </Typography>
                      <List>
                        {porMedioPago.map((medio, index) => (
                          <React.Fragment key={medio.name}>
                            <ListItem sx={{ px: 0 }}>
                              <Box sx={{ width: "100%" }}>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: "50%",
                                      bgcolor: COLORS[index % COLORS.length],
                                      mr: 2,
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                    {medio.name}
                                  </Typography>
                                  <Chip
                                    label={`${medio.participacion.toFixed(1)}%`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {formatCurrency(medio.value)} • {medio.transacciones} transacciones
                                </Typography>
                              </Box>
                            </ListItem>
                            {index < porMedioPago.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* 
              TAB 4: PERFORMANCE POR UBICACIÓN
              
              Funcionalidad:
              - Análisis de performance por sucursal/punto de venta
              - Gráfico combinado con ventas totales y ticket promedio
              - Ranking de ubicaciones por performance
              - Identificación de puntos de venta más rentables
              
              Visualizaciones:
              - Barras: Ventas totales por ubicación
              - Línea: Ticket promedio por ubicación
              - Lista: Ranking con métricas detalladas
              
              Insights proporcionados:
              - Ubicaciones con mayor volumen de ventas
              - Ticket promedio por punto de venta
              - Cantidad de transacciones por ubicación
              - Identificación de oportunidades de mejora
            */}
              <TabPanel value={tabValue} index={3}>
                <Grid container spacing={3} sx={{ p: 3 }}>
                  <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                        📍 Performance por Ubicación
                      </Typography>
                      <ResponsiveContainer width="100%" height={400}>
                        <ComposedChart data={porUbicacion} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis
                            dataKey="name"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fontSize: 11, fill: "#64748b" }}
                            interval={0}
                          />
                          <YAxis
                            yAxisId="ventas"
                            tickFormatter={(value) => formatCurrency(value)}
                            width={100}
                            tick={{ fill: "#64748b" }}
                          />
                          <YAxis
                            yAxisId="ticket"
                            orientation="right"
                            tickFormatter={(value) => formatCurrency(value)}
                            tick={{ fill: "#64748b" }}
                          />
                          <Tooltip
                            formatter={(value, name) => [
                              formatCurrency(value),
                              name === "value" ? "Ventas Totales" : "Ticket Promedio",
                            ]}
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid #e2e8f0",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Bar
                            yAxisId="ventas"
                            dataKey="value"
                            fill="#06B6D4"
                            radius={[4, 4, 0, 0]}
                            name="Ventas Totales"
                          >
                            {porUbicacion?.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                          <Line
                            yAxisId="ticket"
                            type="monotone"
                            dataKey="ticketPromedio"
                            stroke="#F59E0B"
                            strokeWidth={3}
                            dot={{ fill: "#F59E0B", strokeWidth: 2, r: 4 }}
                            name="Ticket Promedio"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", height: "100%" }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                        🏪 Ranking de Ubicaciones
                      </Typography>
                      <List sx={{ maxHeight: 350, overflow: "auto" }}>
                        {porUbicacion
                          ?.sort((a, b) => b.value - a.value)
                          .map((ubicacion, index) => (
                            <React.Fragment key={ubicacion.name}>
                              <ListItem sx={{ px: 0 }}>
                                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                  <Chip
                                    label={index + 1}
                                    size="small"
                                    sx={{
                                      bgcolor: COLORS[index % COLORS.length],
                                      color: "white",
                                      fontWeight: 700,
                                      mr: 2,
                                      minWidth: 32,
                                    }}
                                  />
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                                      {ubicacion.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatCurrency(ubicacion.value)} • {ubicacion.transacciones} transacciones
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Ticket promedio: {formatCurrency(ubicacion.ticketPromedio)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </ListItem>
                              {index < porUbicacion.length - 1 && <Divider />}
                            </React.Fragment>
                          ))}
                      </List>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>

              {hasChannelData && (
                <TabPanel value={tabValue} index={4}>
                  <Grid container spacing={3} sx={{ p: 3 }}>
                    <Grid item xs={12} lg={8}>
                      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                          🏪 Performance por Canal de Entidad
                        </Typography>
                        <ResponsiveContainer width="100%" height={400}>
                          <ComposedChart data={porCanal} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis
                              dataKey="name"
                              angle={-45}
                              textAnchor="end"
                              height={80}
                              tick={{ fontSize: 11, fill: "#64748b" }}
                              interval={0}
                            />
                            <YAxis
                              yAxisId="ventas"
                              tickFormatter={(value) => formatCurrency(value)}
                              width={100}
                              tick={{ fill: "#64748b" }}
                            />
                            <YAxis yAxisId="transacciones" orientation="right" tick={{ fill: "#64748b" }} />
                            <Tooltip
                              formatter={(value, name) => [
                                name === "value" ? formatCurrency(value) : value,
                                name === "value"
                                  ? "Ventas Totales"
                                  : name === "transacciones"
                                    ? "Transacciones"
                                    : "Ticket Promedio",
                              ]}
                              contentStyle={{
                                backgroundColor: "white",
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              }}
                            />
                            <Bar
                              yAxisId="ventas"
                              dataKey="value"
                              fill="#8B5CF6"
                              radius={[4, 4, 0, 0]}
                              name="Ventas Totales"
                            >
                              {porCanal.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Bar>
                            <Line
                              yAxisId="transacciones"
                              type="monotone"
                              dataKey="transacciones"
                              stroke="#EF4444"
                              strokeWidth={3}
                              dot={{ fill: "#EF4444", strokeWidth: 2, r: 4 }}
                              name="Transacciones"
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", height: "100%" }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                          📊 Métricas por Canal
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {porCanal.slice(0, 5).map((canal, index) => (
                            <Box
                              key={canal.name}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: alpha(COLORS[index % COLORS.length], 0.1),
                                border: `1px solid ${alpha(COLORS[index % COLORS.length], 0.2)}`,
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1e293b", mb: 1 }}>
                                {canal.name}
                              </Typography>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: "#64748b" }}>
                                  💰 Ventas: <strong>{formatCurrency(canal.value)}</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#64748b" }}>
                                  📋 Transacciones: <strong>{canal.transacciones}</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#64748b" }}>
                                  🎯 Ticket Promedio: <strong>{formatCurrency(canal.ticketPromedio)}</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#64748b" }}>
                                  👥 Clientes Únicos: <strong>{canal.clientesUnicos}</strong>
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Channel Distribution Pie Chart */}
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                          🥧 Distribución de Ventas por Canal
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={porCanal}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {porCanal.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [formatCurrency(value), "Ventas"]} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Paper>
                    </Grid>

                    {/* Channel Efficiency Analysis */}
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                          ⚡ Eficiencia por Canal
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart
                            data={porCanal}
                            layout="vertical"
                            margin={{ top: 20, right: 30, left: 80, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis
                              type="number"
                              tickFormatter={(value) => formatCurrency(value)}
                              tick={{ fill: "#64748b" }}
                            />
                            <YAxis
                              dataKey="name"
                              type="category"
                              width={100}
                              tick={{ fill: "#64748b", fontSize: 11 }}
                            />
                            <Tooltip
                              formatter={(value) => [formatCurrency(value), "Ticket Promedio"]}
                              contentStyle={{
                                backgroundColor: "white",
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              }}
                            />
                            <Bar dataKey="ticketPromedio" radius={[0, 4, 4, 0]}>
                              {porCanal.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Paper>
                    </Grid>
                  </Grid>
                </TabPanel>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      {/* 
        FOOTER DEL MODAL
        
        Funcionalidad:
        - Contenedor para acciones adicionales
        - Espacio para mensajes o información adicional
      */}
    </Dialog>
  )
}

export default GraficasVentasModal
